import {
  Box,
  Button,
  HStack,
  Heading,
  Image,
  Link,
  Text,
  VStack,
  useBreakpointValue
} from "@chakra-ui/react"
import { flattenEdges } from "@pathwright/utils"
import { CarouselGallery } from "@pathwright/ux/src/components/gallery/Gallery"
import { Link as ReactRouterLink } from "react-router-dom"
import { useResourceDisplayPrice } from "../library/browse/utils"
import { getStoreResourceUrl } from "../utils/urls"
import CoverPageDivider from "./CoverPageDivider"
import { useCoverPageContext } from "./CoverPageProvider"
import { COVER_PAGE_PADDING } from "./constants"
import { CoverPageResource } from "./types"

const getContributorLine = (resource: CoverPageResource) => {
  let contributorLine = []

  const total = resource?.contributors?.total || 0
  const contributors = flattenEdges(resource?.contributors) || []

  if (!contributors?.length) return ""

  contributors.slice(0, 2).forEach((a) => {
    contributorLine.push(a.full_name)
  })
  if (total > 2) {
    contributorLine.push(`+${total - 2}`)
  }
  return contributorLine.join(", ")
}

const RelatedResource = ({ resource }: { resource: CoverPageResource }) => {
  const displayPrice = useResourceDisplayPrice(resource)
  const getResourceTo = (resource: CoverPageResource) => ({
    pathname: getStoreResourceUrl(resource.slug),
    state: { setResourceLaunchedFrom: true }
  })

  const contributorLine = getContributorLine(resource)

  return (
    <VStack
      borderRadius="card"
      shadow="card"
      bg="background"
      gap={0}
      h="100%"
      overflow="hidden"
      transition="all 0.2s"
      _hover={{
        shadow: "cardHover"
      }}
    >
      <Link
        as={ReactRouterLink}
        to={getResourceTo(resource)}
        _hover={{
          "*": {
            textDecoration: "none"
          },
          img: {
            transform: "scale(1.03)"
          }
        }}
      >
        <Box w="100%" overflow="hidden" h="160px">
          <Image
            src={resource.image}
            alt={resource.name}
            borderTopRadius="card"
            maxW="100%"
            objectFit="cover"
            transition="transform 0.2s ease-in-out"
          />
        </Box>
        <VStack
          align="start"
          w="full"
          gap={0}
          p={"10px 10px"}
          minH="100px"
          bg="background"
        >
          <Heading
            as="h6"
            size="h6"
            my={0}
            className="theme-heading theme-heading-bold"
            noOfLines={contributorLine || displayPrice ? 2 : 3}
          >
            {resource.name}
          </Heading>
          {contributorLine && (
            <Text
              my={0}
              textAlign="left"
              fontSize="sm"
              color="blackAlpha.500"
              noOfLines={1}
            >
              {contributorLine}
            </Text>
          )}
          {displayPrice && (
            <Text
              my={0}
              textAlign="left"
              fontSize="sm"
              color="brand.500"
              noOfLines={1}
            >
              {displayPrice}
            </Text>
          )}
        </VStack>
      </Link>
    </VStack>
  )
}

const CoverPageRelated = () => {
  const categories = useCoverPageContext((v) => v.categories)
  const relatedSectionRef = useCoverPageContext((v) => v.relatedSectionRef)
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)
  const relatedResources = useCoverPageContext((v) => v.relatedResources)

  const itemsPerRow = useBreakpointValue({
    base: 1,
    sm: 2,
    md: 3
  })

  if (!relatedResources?.length) return null

  return (
    <>
      <VStack
        w="100%"
        // px={COVER_PAGE_PADDING}
        align="start"
        ref={relatedSectionRef}
        overflow="hidden"
      >
        {!immersiveActive && (
          <CoverPageDivider horizontalMargin={"0"} verticalMargin={"20px"} />
        )}
        {categories?.length ? (
          <>
            {!immersiveActive && (
              <Heading
                as="h3"
                size="h7"
                mt={0}
                px={COVER_PAGE_PADDING}
                mb="10px"
                color="PRIMARY_GRAY"
                className="theme-heading theme-heading-semibold"
              >
                More in{" "}
              </Heading>
            )}
            <HStack
              justify="start"
              align="center"
              wrap="wrap"
              spacing={2}
              px={COVER_PAGE_PADDING}
              overflowX="auto"
              mb="20px"
            >
              {categories.map((category) => (
                <Button
                  key={category.id}
                  as={ReactRouterLink}
                  size="sm"
                  textDecoration="none"
                  _hover={{
                    textDecoration: "none"
                  }}
                  to={`/library/?category=${category.name}`}
                  borderRadius="card"
                  px={4}
                  fontSize="15px"
                  fontWeight="500"
                >
                  {category.name}
                </Button>
              ))}
            </HStack>
          </>
        ) : null}
        <Box
          w="100%"
          sx={{
            ".carousel-container": {
              paddingBottom: "2rem"
            },
            ".carousel-item": {
              maxWidth: "280px"
            }
          }}
        >
          <CarouselGallery
            items={relatedResources}
            gap={0}
            spacingY="0"
            spacingX={COVER_PAGE_PADDING}
            enableStagger
            itemsPerRow={itemsPerRow}
            itemsFillWidth={false}
            renderItem={(resource: CoverPageResource, { index }) => (
              <Box
                key={resource.id}
                py={2}
                pl={index === 0 ? 1 : 3}
                pr={index === relatedResources.length - 1 ? 1 : 3}
                h="100%"
                sx={{
                  ".roundedTop.roundedBottom.roundedLeft.roundedRight": {
                    borderRadius: "var(--chakra-radii-card)",
                    overflow: "hidden"
                  }
                }}
              >
                <RelatedResource resource={resource} />
              </Box>
            )}
          />
        </Box>
      </VStack>
    </>
  )
}
export default CoverPageRelated
